/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { Icon } from '@fluentui/react/lib/Icon';
import { Image } from '@fluentui/react/lib/Image';
import { Stack } from '@fluentui/react/lib/Stack';
import { getTheme } from '@fluentui/react/lib/Styling';
import { Text } from '@fluentui/react/lib/Text';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import { AnalyzedSentence } from './AnalyzedSentence';
const { palette } = getTheme();
const headerTextStyles = classNamesFunction()({ root: { lineHeight: 20, fontWeight: 600 } });
const descriptionIconStyles = classNamesFunction()({
    root: { fontSize: '12px', lineHeight: '12px', cursor: 'pointer' }
});
const containerStyles = classNamesFunction()({
    root: {
        border: `1px solid ${palette.neutralQuaternaryAlt}`,
        height: '450px'
    }
});
const headerContainerStyles = classNamesFunction()({
    root: {
        padding: '10px 12px 2px 12px',
        borderBottom: `1px solid ${palette.neutralQuaternaryAlt}`
    }
});
const getDescriptionItem = descriptionText =>
    React.createElement(
        TooltipHost,
        { content: descriptionText },
        React.createElement(Icon, { tabIndex: 0, iconName: 'Info', styles: descriptionIconStyles })
    );
export const AnalyzedConversationViewer = props => {
    var _a, _b, _c;
    const { result, labels } = props;
    const { hostname } = window.location;
    const isMediumOrGreater = useMediaQuery({ query: `(min-width: 480px)` });
    const [hidePii, setHidePii] = React.useState(true);
    const textMessages = Object.assign(
        {
            onText: 'On',
            offText: 'Off',
            hidePii: 'Hide PII',
            sentimentHeader: 'Sentiment',
            transcriptHeader: 'Transcript',
            piiDescription: 'Sensitive information across several pre-defined categories is detected and redacted from the transcript.',
            sentimentDescription:
                'Sensitive information across several pre-defined categories is detected and redacted from the transcript.'
        },
        labels
    );
    return React.createElement(
        Stack,
        { styles: containerStyles },
        React.createElement(
            Stack,
            { horizontal: isMediumOrGreater, horizontalAlign: 'space-between', styles: headerContainerStyles },
            React.createElement(
                Stack,
                { horizontal: true },
                React.createElement(
                    Stack,
                    { verticalAlign: 'baseline', horizontal: true, styles: { root: { width: 100 } }, tokens: { childrenGap: 5 } },
                    React.createElement(
                        Text,
                        { variant: 'medium', styles: headerTextStyles, 'aria-label': textMessages.sentimentHeader },
                        textMessages.sentimentHeader
                    ),
                    getDescriptionItem((_a = textMessages.sentimentDescription) !== null && _a !== void 0 ? _a : '')
                ),
                React.createElement(
                    Text,
                    { variant: 'medium', 'aria-label': textMessages.transcriptHeader, styles: headerTextStyles },
                    textMessages.transcriptHeader
                )
            ),
            React.createElement(Toggle, {
                inlineLabel: true,
                checked: hidePii,
                onText: textMessages.onText,
                offText: textMessages.offText,
                styles: { label: { padding: 0 } },
                onChange: (_, checked) => setHidePii(Boolean(checked)),
                label: React.createElement(
                    Stack,
                    { verticalAlign: 'baseline', horizontal: true, tokens: { childrenGap: 5 } },
                    React.createElement(
                        Text,
                        { variant: 'medium', 'aria-label': textMessages.hidePii, styles: headerTextStyles },
                        textMessages.hidePii
                    ),
                    getDescriptionItem((_b = textMessages.piiDescription) !== null && _b !== void 0 ? _b : '')
                )
            })
        ),
        React.createElement(
            Stack,
            { grow: true, styles: { root: { overflowY: 'auto' } } },
            !((_c = result === null || result === void 0 ? void 0 : result.sentences) === null || _c === void 0 ? void 0 : _c.length) &&
                React.createElement(
                    Stack,
                    { grow: true, horizontalAlign: 'center', verticalAlign: 'center' },
                    React.createElement(Image, {
                        alt: 'Empty state icon',
                        src: `https://${hostname}/assets/images/customText/newEmptyState.svg`
                    })
                ),
            result.sentences.map(sentence => React.createElement(AnalyzedSentence, { sentence: sentence, hidePii: hidePii }))
        )
    );
};
